import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCookieBite, FaTimes } from 'react-icons/fa';

// ===== CORES =====
const colors = {
  background: '#161616',
  backgroundLight: '#1a1a1a',
  primary: '#00A8E8',
  primaryDark: '#0077B6',
  white: '#FFFFFF',
  textPrimary: '#F5F5F5',
  textSecondary: '#B8B8B8',
  textMuted: '#888888',
  border: 'rgba(255, 255, 255, 0.1)',
};

// ===== ANIMAÇÕES =====
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
`;

// ===== STYLED COMPONENTS =====
const CookieBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${colors.background};
  border-top: 1px solid ${colors.border};
  padding: 1rem 1.5rem;
  z-index: 9999;
  animation: ${props => props.$closing ? slideDown : slideUp} 0.4s ease forwards;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CookieContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
`;

const CookieTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const CookieIcon = styled.div`
  font-size: 1.75rem;
  color: ${colors.primary};
  flex-shrink: 0;
`;

const CookieText = styled.p`
  font-size: 0.85rem;
  color: ${colors.textSecondary};
  line-height: 1.5;
  margin: 0;
  
  a {
    color: ${colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CookieButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const AcceptButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const RejectButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: transparent;
  color: ${colors.textMuted};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${colors.textSecondary};
    color: ${colors.textSecondary};
  }
`;

// ===== COMPONENTE =====
const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Verifica se já aceitou/rejeitou cookies
    const cookieConsent = localStorage.getItem('cadpro_cookie_consent');
    
    if (!cookieConsent) {
      // Aguarda 1.5s para mostrar o banner (não atrapalha o carregamento inicial)
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setIsClosing(true);
    localStorage.setItem('cadpro_cookie_consent', 'accepted');
    localStorage.setItem('cadpro_cookie_date', new Date().toISOString());
    
    setTimeout(() => {
      setIsVisible(false);
    }, 400);
  };

  const handleReject = () => {
    setIsClosing(true);
    localStorage.setItem('cadpro_cookie_consent', 'rejected');
    localStorage.setItem('cadpro_cookie_date', new Date().toISOString());
    
    setTimeout(() => {
      setIsVisible(false);
    }, 400);
  };

  if (!isVisible) return null;

  return (
    <CookieBanner $closing={isClosing}>
      <CookieContent>
        <CookieTextWrapper>
          <CookieIcon>
            <FaCookieBite />
          </CookieIcon>
          <CookieText>
            Utilizamos cookies para melhorar sua experiência de navegação e analisar o tráfego do site. 
            Ao continuar, você concorda com nossa{' '}
            <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
              Política de Privacidade
            </a>.
          </CookieText>
        </CookieTextWrapper>
        
        <CookieButtons>
          <RejectButton onClick={handleReject}>
            Rejeitar
          </RejectButton>
          <AcceptButton onClick={handleAccept}>
            Aceitar Cookies
          </AcceptButton>
        </CookieButtons>
      </CookieContent>
    </CookieBanner>
  );
};

export default CookieConsent;